(() => {
  if (window.AnsleyInfiniteScroll) return;

  class AnsleyInfiniteScroll {
    constructor(root) {
      this.root = root;
      this.container = root.closest('#ProductGridContainer');
      this.list = this.container?.querySelector('[data-ansley-product-list]');
      this.nextLink = root.querySelector('[data-ansley-infinite-next]');
      this.status = root.querySelector('[data-ansley-infinite-status]');
      this.message = root.querySelector('[data-ansley-infinite-message]');
      this.retryButton = root.querySelector('[data-ansley-infinite-retry]');
      this.sectionId = root.dataset.sectionId;
      this.itemLabel = root.dataset.itemLabel || 'products';
      this.loadingMessage = root.dataset.loadingMessage || 'Loading more products…';
      this.endMessage = root.dataset.endMessage || 'You’ve reached the end.';
      this.isLoading = false;

      if (!this.container || !this.list || !this.nextLink || !this.sectionId) return;

      this.loadNextPage = this.loadNextPage.bind(this);
      this.retryButton?.addEventListener('click', this.loadNextPage);
      this.nextLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.loadNextPage();
      });

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) this.loadNextPage();
          },
          { rootMargin: '500px 0px' }
        );
        this.observer.observe(this.nextLink);
      } else {
        this.nextLink.classList.add('is-visible-fallback');
      }

      root.dataset.ansleyInfiniteInitialized = 'true';
    }

    setStatus(message, state) {
      if (this.statusTimer) window.clearTimeout(this.statusTimer);
      if (this.message) this.message.textContent = message;
      if (!this.status) return;

      this.status.classList.toggle('is-active', state === 'loading' || state === 'error' || state === 'complete');
      this.status.classList.toggle('is-error', state === 'error');
      this.status.classList.toggle('is-complete', state === 'complete');
    }

    prepareNewItems(items) {
      items.forEach((item, index) => {
        if (item.classList.contains('scroll-trigger')) {
          item.classList.add('scroll-trigger--cancel');
          item.style.setProperty('--animation-order', index + 1);
        }
      });
    }

    async loadNextPage() {
      if (this.isLoading || !this.nextLink) return;

      const nextUrl = this.nextLink.getAttribute('href');
      if (!nextUrl) return;

      this.isLoading = true;
      this.list.setAttribute('aria-busy', 'true');
      this.retryButton?.setAttribute('hidden', '');
      this.nextLink.classList.remove('is-visible-fallback');
      this.observer?.unobserve(this.nextLink);
      this.setStatus(this.loadingMessage, 'loading');

      try {
        const url = new URL(nextUrl, window.location.origin);
        url.searchParams.set('section_id', this.sectionId);

        const response = await fetch(url.toString(), {
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });

        if (!response.ok) throw new Error(`Request failed with status ${response.status}`);

        const documentFromResponse = new DOMParser().parseFromString(await response.text(), 'text/html');
        const newList = documentFromResponse.querySelector('[data-ansley-product-list]');
        const newRoot = documentFromResponse.querySelector('[data-ansley-infinite-scroll]');
        const newNextLink = newRoot?.querySelector('[data-ansley-infinite-next]');

        if (!newList) throw new Error('The next product page did not contain a product list.');

        const newItems = Array.from(newList.children);
        this.prepareNewItems(newItems);
        newItems.forEach((item) => this.list.appendChild(item));

        if (newNextLink?.getAttribute('href')) {
          this.nextLink.setAttribute('href', newNextLink.getAttribute('href'));
          this.setStatus(`${newItems.length} more ${this.itemLabel} loaded.`, 'complete');
          this.statusTimer = window.setTimeout(() => {
            this.status?.classList.remove('is-active', 'is-complete');
          }, 900);
          this.observer?.observe(this.nextLink);
        } else {
          this.nextLink.remove();
          this.nextLink = null;
          this.setStatus(this.endMessage, 'complete');
        }
      } catch (error) {
        console.error('Ansley Effect infinite scroll:', error);
        this.setStatus(`More ${this.itemLabel} could not be loaded automatically.`, 'error');
        this.retryButton?.removeAttribute('hidden');
        this.nextLink?.classList.add('is-visible-fallback');
      } finally {
        this.isLoading = false;
        this.list.removeAttribute('aria-busy');
      }
    }
  }

  function initializeAnsleyInfiniteScroll() {
    document.querySelectorAll('[data-ansley-infinite-scroll]').forEach((root) => {
      if (root.dataset.ansleyInfiniteInitialized !== 'true') new AnsleyInfiniteScroll(root);
    });
  }

  window.AnsleyInfiniteScroll = AnsleyInfiniteScroll;
  window.initAnsleyInfiniteScroll = initializeAnsleyInfiniteScroll;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeAnsleyInfiniteScroll, { once: true });
  } else {
    initializeAnsleyInfiniteScroll();
  }

  document.addEventListener('shopify:section:load', initializeAnsleyInfiniteScroll);
})();
